import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Checkbox } from "./ui/checkbox";
import { Users2, Plus, Trash2, Pencil, Star, Save, X, Search } from "lucide-react";

/**
 * Iter 180 — Clusters admin.
 *
 * CRUD for the `app_clusters` collection. Each cluster has:
 *   • name, description, emoji, color
 *   • a single Liaison spotlight (member picker, like Governor on regions)
 *   • an explicit member list (tick-box picker)
 */
export default function ClustersAdmin() {
    const [clusters, setClusters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    async function reload() {
        setLoading(true);
        try {
            const { data } = await api.get("/clusters");
            setClusters(data || []);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { reload(); }, []);

    async function del(c) {
        if (!window.confirm(`Delete "${c.name}"? This can't be undone.`)) return;
        try {
            await api.delete(`/admin/clusters/${c.id}`);
            toast.success(`Deleted ${c.name}`);
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    }

    function newCluster() {
        setEditing({
            id: "",
            name: "",
            description: "",
            emoji: "👥",
            color: "#0A2463",
            liaison_user_id: "",
            member_ids: [],
            order: (clusters.at(-1)?.order || 100) + 1,
        });
    }

    return (
        <div className="space-y-6" data-testid="clusters-admin">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-xl font-heading font-bold">Clusters</h2>
                    <p className="text-sm text-muted-foreground">
                        Create tight-knit member groupings. Each cluster surfaces a Liaison spotlight on
                        <span className="font-mono mx-1">/clusters</span>.
                    </p>
                </div>
                <Button onClick={newCluster} className="rounded-full" data-testid="admin-clusters-new-btn">
                    <Plus className="h-4 w-4 mr-1.5" /> New cluster
                </Button>
            </div>

            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

            {!loading && clusters.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                    <Users2 className="h-12 w-12 mx-auto text-slate-300" />
                    <p className="mt-3">No clusters yet.</p>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                {clusters.map((c) => (
                    <div
                        key={c.id}
                        className="bg-card border border-border rounded-xl overflow-hidden shadow-warm"
                        data-testid={`admin-cluster-card-${c.id}`}
                    >
                        <div
                            className="px-4 py-3 flex items-center justify-between text-white"
                            style={{ backgroundColor: c.color }}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-xl">{c.emoji || "👥"}</span>
                                <div>
                                    <div className="font-semibold text-sm">{c.name}</div>
                                    <div className="text-[10px] opacity-85">{c.member_count} member{c.member_count === 1 ? "" : "s"}</div>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 space-y-3">
                            {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
                            <div className="text-xs">
                                {c.liaison ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-500/40 px-2 py-0.5 font-medium">
                                        <Star className="h-3 w-3 text-amber-500 fill-amber-400" /> Liaison: {c.liaison.name}
                                    </span>
                                ) : (
                                    <span className="text-muted-foreground">No Liaison assigned</span>
                                )}
                            </div>
                            <div className="flex gap-2 pt-1">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full"
                                    onClick={() => setEditing(c)}
                                    data-testid={`admin-cluster-edit-${c.id}`}
                                >
                                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="rounded-full text-destructive"
                                    onClick={() => del(c)}
                                    data-testid={`admin-cluster-delete-${c.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <ClusterEditor
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}


function ClusterEditor({ initial, onClose, onSaved }) {
    const isNew = !initial.id;
    const [form, setForm] = useState(() => ({ ...initial }));
    const [busy, setBusy] = useState(false);
    const [liaison, setLiaison] = useState(null);
    const [liaisonQuery, setLiaisonQuery] = useState("");
    const [liaisonResults, setLiaisonResults] = useState([]);
    const [memberSearch, setMemberSearch] = useState("");
    const [allMembers, setAllMembers] = useState([]);

    // Preload liaison basic info.
    useEffect(() => {
        if (!form.liaison_user_id) { setLiaison(null); return; }
        api.get(`/members/${form.liaison_user_id}`)
            .then(({ data }) => setLiaison(data))
            .catch(() => setLiaison(null));
    }, [form.liaison_user_id]);

    // Member list (for the tick-box picker).
    useEffect(() => { api.get("/members").then(({ data }) => setAllMembers(data)).catch(() => {}); }, []);

    // Live search for liaison.
    useEffect(() => {
        if (!liaisonQuery.trim()) { setLiaisonResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/members", { params: { q: liaisonQuery } });
                setLiaisonResults(data.slice(0, 8));
            } catch { setLiaisonResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [liaisonQuery]);

    function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    function toggleMember(id) {
        setForm((f) => {
            const has = f.member_ids.includes(id);
            return { ...f, member_ids: has ? f.member_ids.filter((x) => x !== id) : [...f.member_ids, id] };
        });
    }

    const filteredMembers = memberSearch.trim()
        ? allMembers.filter((m) => {
            const q = memberSearch.trim().toLowerCase();
            return (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
        })
        : allMembers;

    async function save() {
        if (!form.name.trim()) return toast.error("Name is required");
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description || "",
                emoji: form.emoji || "👥",
                color: form.color || "#0A2463",
                order: Number(form.order) || 100,
                liaison_user_id: form.liaison_user_id || "",
                member_ids: form.member_ids || [],
            };
            if (isNew) {
                await api.post("/admin/clusters", payload);
                toast.success("Cluster created");
            } else {
                await api.put(`/admin/clusters/${form.id}`, payload);
                toast.success("Cluster updated");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-cluster-editor">
                <DialogHeader>
                    <DialogTitle>{isNew ? "New cluster" : `Edit ${form.name}`}</DialogTitle>
                    <DialogDescription>
                        Clusters are flat groupings of members with their own Liaison spotlight. One member can belong to multiple clusters.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                        <div className="sm:col-span-3">
                            <label className="text-xs font-medium">Name</label>
                            <Input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="admin-cluster-field-name" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Order</label>
                            <Input type="number" value={form.order} onChange={(e) => setField("order", e.target.value)} data-testid="admin-cluster-field-order" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-medium">Description</label>
                        <Textarea rows={2} value={form.description} onChange={(e) => setField("description", e.target.value)} data-testid="admin-cluster-field-description" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">Emoji</label>
                            <Input value={form.emoji} onChange={(e) => setField("emoji", e.target.value)} placeholder="👥" data-testid="admin-cluster-field-emoji" />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Accent color</label>
                            <Input type="color" value={form.color} onChange={(e) => setField("color", e.target.value)} data-testid="admin-cluster-field-color" />
                        </div>
                    </div>

                    {/* Liaison picker */}
                    <div className="rounded-xl border p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm font-semibold">Liaison</div>
                                <div className="text-[11px] text-muted-foreground">Spotlight shown on the public cluster card.</div>
                            </div>
                            {liaison && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => { setField("liaison_user_id", ""); setLiaison(null); }}
                                    className="rounded-full text-xs"
                                    data-testid="admin-cluster-liaison-clear"
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                        {liaison ? (
                            <div className="p-2 rounded-lg bg-amber-50 text-sm border border-amber-500/40 flex items-center gap-2">
                                <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                                <span className="font-medium">{liaison.name}</span>
                                <span className="text-xs text-muted-foreground">{liaison.email}</span>
                            </div>
                        ) : (
                            <div>
                                <Input
                                    value={liaisonQuery}
                                    onChange={(e) => setLiaisonQuery(e.target.value)}
                                    placeholder="Search for a member by name or email…"
                                    data-testid="admin-cluster-liaison-search"
                                />
                                {liaisonResults.length > 0 && (
                                    <div className="mt-1 rounded-lg border max-h-40 overflow-y-auto">
                                        {liaisonResults.map((m) => (
                                            <button
                                                key={m.id}
                                                type="button"
                                                onClick={() => { setField("liaison_user_id", m.id); setLiaisonQuery(""); setLiaisonResults([]); }}
                                                className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center gap-2"
                                                data-testid={`admin-cluster-liaison-pick-${m.id}`}
                                            >
                                                <span className="font-medium">{m.name}</span>
                                                <span className="text-xs text-muted-foreground truncate">{m.email}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Members tick-box picker */}
                    <div className="rounded-xl border p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <div className="text-sm font-semibold">Members ({form.member_ids.length})</div>
                                <div className="text-[11px] text-muted-foreground">Tick everyone who belongs to this cluster.</div>
                            </div>
                            <div className="relative">
                                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={memberSearch}
                                    onChange={(e) => setMemberSearch(e.target.value)}
                                    placeholder="Search…"
                                    className="pl-7 h-8 w-48"
                                    data-testid="admin-cluster-members-search"
                                />
                            </div>
                        </div>
                        <div className="max-h-56 overflow-y-auto rounded-lg border divide-y">
                            {filteredMembers.slice(0, 200).map((m) => {
                                const checked = form.member_ids.includes(m.id);
                                return (
                                    <label
                                        key={m.id}
                                        className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                                        data-testid={`admin-cluster-member-row-${m.id}`}
                                    >
                                        <Checkbox checked={checked} onCheckedChange={() => toggleMember(m.id)} />
                                        <Avatar className="h-6 w-6">
                                            {m.avatar_url && <AvatarImage src={m.avatar_url} />}
                                            <AvatarFallback className="text-[9px]">
                                                {(m.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="font-medium truncate flex-1">{m.name}</span>
                                        <span className="text-[11px] text-muted-foreground truncate">{m.email}</span>
                                    </label>
                                );
                            })}
                            {filteredMembers.length === 0 && (
                                <div className="p-3 text-xs text-muted-foreground text-center">No members match.</div>
                            )}
                        </div>
                    </div>
                </div>
                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">
                        <X className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                    <Button onClick={save} disabled={busy} className="rounded-full" data-testid="admin-cluster-save-btn">
                        <Save className="h-4 w-4 mr-1" /> {busy ? "Saving…" : "Save cluster"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
